import React, { useEffect, useMemo } from 'react';

import { CloudProviders } from '@ariksa/cloud-account';
import { each, filter, includes, toArray } from 'lodash';
import map from 'lodash/map';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { getProviderAlias } from 'components/DataDisplay';
import { IconTypes } from 'components/Icons';
import { DashboardWidget } from 'components/Visualization/CDashboard/Widget/DashboardWidget';
import { useAccessBoundary } from 'containers/App/hooks/useAccessBoundary';
import { useEnvironmentOptions } from 'containers/App/hooks/useEnvironmentOptions';
import { selectDashboardOverview } from 'containers/Dashboard/Overview/selectors';
import { renderResourcesWithIconAndCount } from 'containers/Dashboard/utils/utils';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';
import { selectSharedState } from 'containers/SharedState/selectors';

export const PolicyManagement: React.FC = () => {
  const { policyManagement } = useSelector(selectDashboardOverview);
  const { blueprints } = useSelector(selectSharedState);
  const { environmentMapping } = useEnvironmentOptions();
  const { environmentId, accountId } = useAccessBoundary();
  const { policies, workflows } = policyManagement;
  const navigate = useNavigate();
  const [customBlueprints, setCustomBlueprints] = React.useState(0);
  const [clouds, setClouds] = React.useState<Record<string, number>>({});
  const { accountMapping } = useCloudAccountId();
  const [alertingAccounts, setAlertingAccounts] = React.useState<string[]>([]);
  const [alertingEnvs, setAlertingEnvs] = React.useState<string[]>([]);

  //get custom blueprints count
  useEffect(() => {
    const customBlueprints = filter(
      blueprints.data,
      o => o?.created_by !== 'system',
    );
    setCustomBlueprints(customBlueprints.length);
  }, [blueprints.data]);

  //set alerting environments
  useEffect(() => {
    const alertingEnvs = new Set<string>();
    each(blueprints.data, o => {
      each(o.alerting_environments, (env: string) => alertingEnvs.add(env));
    });
    setAlertingEnvs(toArray(alertingEnvs) as string[]);
  }, [blueprints.data]);

  //set alerting accounts
  useEffect(() => {
    if (includes(alertingEnvs, environmentId))
      setAlertingAccounts(
        filter(toArray(environmentMapping?.[environmentId!]?.account_ids), a =>
          includes(
            [CloudProviders.Aws, CloudProviders.Gcp, CloudProviders.Azure],
            accountMapping?.[a]?.cloud_type,
          ),
        ),
      );
  }, [environmentId, environmentMapping, alertingEnvs, accountMapping]);

  //set clouds
  useEffect(() => {
    let clouds: Record<string, number> = {};
    each(alertingAccounts, (o: string) => {
      if (!!clouds[accountMapping?.[o]?.cloud_type!])
        clouds[accountMapping?.[o]?.cloud_type!] += 1;
      else clouds[accountMapping?.[o]?.cloud_type!] = 1;
    });
    setClouds(clouds);
  }, [alertingAccounts, accountMapping]);

  const items = useMemo(
    () => [
      {
        label: 'Accounts with alerting for issues',
        count: !!accountId
          ? alertingAccounts?.includes(accountId)
            ? 1
            : 0
          : alertingAccounts?.length,
        //isLoading: .isLoading,
        iconType: IconTypes.Account,
        onClick: () => navigate('/policy-hub/blueprint'),
      },
      {
        label: 'Policies enforced',
        count: policies.data?.policies || 0,
        isLoading: policies.isLoading,
        iconType: IconTypes.List,
        onClick: () => navigate('/policy-hub/policy/cloud'),
      },
      {
        label: 'Custom policy blueprints',
        count: customBlueprints,
        isLoading: blueprints.isLoading,
        iconType: IconTypes.Blueprint,
        onClick: () => navigate('/policy-hub/policy/cloud'),
      },
      {
        label: 'Resource exceptions',
        count: policies.data?.resource_exceptions || 0,
        isLoading: policies.isLoading,
        iconType: IconTypes.Exception,
        onClick: () => navigate('/policy-hub/blueprint'),
      },
      {
        label: 'Policy exceptions',
        count: policies.data?.policy_exceptions || 0,
        isLoading: policies.isLoading,
        iconType: IconTypes.Exception,
        onClick: () => navigate('/policy-hub/blueprint'),
      },
      {
        label: 'Active workflows',
        count: workflows.data?.length || 0,
        isLoading: workflows.isLoading,
        iconType: IconTypes.Documents,
        onClick: () => navigate('/policy-hub/workflow'),
      },
    ],
    [
      policies.data,
      policies.isLoading,
      blueprints.isLoading,
      workflows.data,
      workflows.isLoading,
      environmentId,
      accountId,
      environmentMapping,
      navigate,
      customBlueprints,
      // Was missing, so the alerting-account count stayed stale until another dep changed.
      alertingAccounts,
    ],
  );

  return (
    <DashboardWidget
      label="Policy Management"
      subHeader={
        map(clouds, (value, key) => `${value} ${getProviderAlias(key)}`).join(
          ', ',
        ) + ' accounts'
      }
      content={renderResourcesWithIconAndCount(items)}
      styles={{ contentWrapper: { px: 0, pb: 0 } }}
    />
  );
};
